import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { trpc } from '@/lib/trpc';

export default function AcceptOrgInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const token = params.get('token') || '';
  const [status, setStatus] = useState<'idle' | 'preview' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');
  const [orgName, setOrgName] = useState<string>('');
  const [role, setRole] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  const acceptMutation = trpc.users.acceptOrgInvite.useMutation({
    onSuccess: () => {
      setStatus('success');
      setMessage(t('accessGranted'));
    },
    onError: (err) => {
      setStatus('error');
      setMessage(err.message || t('failedToAcceptOrgInvite'));
    },
  });
  const previewQuery = trpc.users.previewOrgInvite.useQuery({ token }, { enabled: !!token });

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(t('missingInviteLink'));
      return;
    }
    if (previewQuery.data) {
      const d = previewQuery.data;
      setEmail(d.email);
      setOrgName(d.organizationName || '');
      setRole(d.role || '');
      if (d.status === 'valid') {
        setStatus('preview');
        setMessage(t('invitedToJoinOrg'));
      } else if (d.status === 'expired') {
        setStatus('error');
        setMessage(t('inviteExpiredResend'));
      } else if (d.status === 'used') {
        setStatus('error');
        setMessage(t('inviteAlreadyUsed'));
      } else {
        setStatus('error');
        setMessage(t('invalidInviteLink'));
      }
    }
  }, [token, previewQuery.data, t]);

  const onAccept = () => {
    if (!token) return;
    acceptMutation.mutate({ token });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            {status === 'success' ? (
              <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
            ) : status === 'error' ? (
              <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-yellow-600" />
              </div>
            ) : null}
          </div>
          <CardTitle className="text-2xl text-center">{t('joinStore')}</CardTitle>
          <CardDescription className="text-center">
            {status === 'idle' && t('validatingInvite')}
            {status !== 'idle' && message}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'error' && (
            <Alert variant="destructive">
              <AlertDescription>
                {message}
              </AlertDescription>
            </Alert>
          )}
          {status === 'preview' && (
            <div className="text-sm text-center space-y-1">
              <div>
                <Trans
                  i18nKey="auth:acceptingInviteTo"
                  values={{ orgName }}
                  components={{ strong: <strong /> }}
                />
                {role ? (
                  <Trans
                    i18nKey="auth:asRole"
                    values={{ role }}
                    components={{ strong: <strong /> }}
                  />
                ) : null}
              </div>
              {email ? (
                <div>
                  <Trans
                    i18nKey="auth:forAccount"
                    values={{ email }}
                    components={{ strong: <strong /> }}
                  />
                </div>
              ) : null}
            </div>
          )}
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={() => navigate('/')}>{t('goHome')}</Button>
            {status === 'preview' ? (
              <Button onClick={onAccept} disabled={acceptMutation.isPending}>
                {acceptMutation.isPending ? t('joining') : t('joinStore')}
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')}>{t('goToLogin')}</Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
